/**
 * i18n helpers that delegate to window.wp.i18n — the @wordpress/i18n instance
 * that WordPress controls and injects translations into via wp_set_script_translations().
 *
 * Using window.wp.i18n directly (rather than a bundled copy) ensures that
 * wp.i18n.setLocaleData() calls made by WordPress's inline translation script
 * are visible to our i18n.__() calls at render time.
 *
 * Falls back gracefully (returns the original string) when wp.i18n is not
 * available (e.g. local dev outside WordPress, or wp-i18n not enqueued).
 *
 * IMPORTANT: call these as members — i18n.__( "…", "accessibility-widget" ) —
 * never re-export or alias the functions as bare identifiers. Minifiers rename
 * bare identifiers (the old `__` import became `__$1(` in the dist bundle),
 * which makes the strings invisible to wp-cli i18n, Loco Translate and
 * translate.wordpress.org. Property names survive minification, so the member
 * form stays extractable in the built bundle. Guarded by
 * tests/i18n-regression.test.ts.
 */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type WpI18nGlobal = Record<string, (...args: any[]) => any>;

function wpI18n(): WpI18nGlobal | undefined {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (window as any)?.wp?.i18n as WpI18nGlobal | undefined;
}

export const i18n = {
  __(text: string, domain?: string): string {
    return wpI18n()?.__?.(text, domain) ?? text;
  },

  _n(single: string, plural: string, count: number, domain?: string): string {
    return wpI18n()?._n?.(single, plural, count, domain) ?? (count === 1 ? single : plural);
  },

  _x(text: string, context: string, domain?: string): string {
    return wpI18n()?._x?.(text, context, domain) ?? text;
  },

  sprintf(format: string, ...args: unknown[]): string {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (wpI18n()?.sprintf as any)?.(format, ...args) ?? format;
  },
};

export const TEXT_DOMAIN = "accessibility-widget";
